import { z } from 'zod';
import { CONFIDENCE_THRESHOLDS } from '../config/confidence';

/**
 * Coarse per-field workflow state, distinct from the fine-grained confidence
 * *band* (high/medium/low — see config/confidence.ts) used just for badge
 * color. `status` instead drives pipeline/UI behavior:
 *  - 'missing'        no value could be confidently identified (or Gemini
 *                      returned null) — Review UI must prompt for manual entry.
 *  - 'ok'              a value is present with confidence >= CONFIDENCE_THRESHOLDS.ok.
 *  - 'low_confidence'  a value is present but below that bar (covers both the
 *                      medium and low confidence bands) — Review UI highlights it.
 *  - 'user_edited'     the user has typed a correction; the original AI
 *                      confidence/evidence no longer applies.
 */
export const ExtractionStatusSchema = z.enum(['ok', 'low_confidence', 'missing', 'user_edited']);
export type ExtractionStatus = z.infer<typeof ExtractionStatusSchema>;

export function statusForConfidence(confidence: number, hasValue: boolean): ExtractionStatus {
  if (!hasValue) return 'missing';
  return confidence >= CONFIDENCE_THRESHOLDS.ok ? 'ok' : 'low_confidence';
}

export function extractionFieldSchema<T extends z.ZodTypeAny>(valueSchema: T) {
  return z.object({
    value: valueSchema.nullable(),
    confidence: z.number().min(0).max(1),
    /** Short quote from the bill the model based this value on, for user trust/debugging. */
    evidence: z.string().nullable().optional(),
    status: ExtractionStatusSchema,
  });
}

export interface ExtractionField<T> {
  value: T | null;
  confidence: number;
  evidence?: string | null;
  status: ExtractionStatus;
}

export const PaymentMethodSchema = z.enum(['cash', 'electronic', 'unknown']);
export type PaymentMethod = z.infer<typeof PaymentMethodSchema>;

/**
 * The dynamic, per-bill fields the IRD Enrollment form needs (verified field
 * list — see docs/ird-site-analysis.md). Bill number and PAN are always
 * strings, never numbers, so leading zeros / exact digit sequences survive
 * intact through the whole pipeline.
 */
export const BillDataSchema = z.object({
  billNumber: extractionFieldSchema(z.string().min(1)),
  sellerPan: extractionFieldSchema(z.string().regex(/^\d{9}$/, 'PAN must be exactly 9 digits')),
  /** Both are always populated together once a date is known — see date/bikramSambat.ts. */
  billDateAd: extractionFieldSchema(z.string().regex(/^\d{4}-\d{2}-\d{2}$/)),
  billDateBs: extractionFieldSchema(z.string().regex(/^\d{4}-\d{2}-\d{2}$/)),
  amount: extractionFieldSchema(z.number().positive()),
  paymentMethod: extractionFieldSchema(PaymentMethodSchema),
  /** Context only — not itself an IRD form field, but helps the user sanity-check the scan. */
  sellerName: extractionFieldSchema(z.string()),
});
export type BillData = z.infer<typeof BillDataSchema>;

export type BillDataFieldKey = keyof BillData;

export const BillSubmissionStateSchema = z.enum(['draft', 'ready', 'marked_submitted']);
export type BillSubmissionState = z.infer<typeof BillSubmissionStateSchema>;

/** IndexedDB row shape (client-only — see lib/db/billsRepo.ts, added in Phase 5). */
export interface BillRecord {
  id: string;
  createdAt: number;
  updatedAt: number;
  billData: BillData;
  submissionState: BillSubmissionState;
  submittedAt?: number;
}
